// Portfolio Administration v1.0 PR 4a §7.7 — Active vendor assignments
// table for a single loan.
//
// In v1.0 the per-loan "assignment" is a LoanPartyAssignment whose
// counterpartyType=Vendor. The richer VendorAssignment / VendorAgreement /
// VFILedgerEntry entities (with monthly fees, executed agreement, VFI
// status, performance score) are not in the PR 1 schema; the columns that
// depend on them render "—" with a TODO. See spec §7.7 for the target
// shape — this gets wired end-to-end in the Vendor Program v3.0 module.

import { Link } from "react-router-dom";
import { Table, THead, TBody, TR, TH, TD, Badge } from "@/design-system/components/ui";
import type {
  LoanPartyAssignmentDto,
  VendorDto,
} from "@/api/portfolioAdmin/types";
import { useDateFormatter } from "@/utils/datetime";

interface ActiveVendorAssignmentsTableProps {
  vendorParties: LoanPartyAssignmentDto[];
  vendorRegistry: VendorDto[];
  onRowClick: (assignment: LoanPartyAssignmentDto) => void;
  /** Step 5 W1 — assignment ids that have a LIVE vendor connection. Undefined
   *  while the connections read is in flight, which renders no Connection
   *  cell at all rather than briefly claiming every row is unconnected. */
  connectedAssignmentIds?: ReadonlySet<string>;
}

export function ActiveVendorAssignmentsTable({
  vendorParties,
  vendorRegistry,
  onRowClick,
  connectedAssignmentIds,
}: ActiveVendorAssignmentsTableProps) {
  const { date } = useDateFormatter();
  const byId = new Map(vendorRegistry.map((v) => [v.id, v]));
  if (vendorParties.length === 0) {
    return (
      <p className="text-small text-gray-mid italic m-0">
        No vendor assignments on this loan yet.
      </p>
    );
  }
  return (
    <Table data-testid="vendors-assignments-table">
      <THead>
        <TR>
          <TH className="text-white-pure">Vendor</TH>
          <TH className="text-white-pure">Type</TH>
          <TH className="text-white-pure">Role on loan</TH>
          <TH className="text-white-pure">Agreement</TH>
          <TH className="text-white-pure">Monthly fee</TH>
          <TH className="text-white-pure">Assigned</TH>
          <TH className="text-white-pure">VFI</TH>
          <TH className="text-white-pure">Connection</TH>
          <TH className="text-white-pure">Status</TH>
        </TR>
      </THead>
      <TBody>
        {vendorParties.map((p) => {
          const vendor = byId.get(p.counterpartyId);
          return (
            <TR
              key={p.id}
              onClick={() => onRowClick(p)}
              className="cursor-pointer"
              data-testid={`vendors-assignment-row-${p.id}`}
            >
              <TD>
                {vendor ? (
                  <Link
                    to={`/portfolio-admin/vendors/${vendor.id}`}
                    className="text-gold-shadow hover:underline"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`vendor-record-link-${vendor.id}`}
                  >
                    {vendor.name}
                  </Link>
                ) : (
                  p.counterpartyId
                )}
              </TD>
              <TD>{vendor?.vendorType ?? "—"}</TD>
              <TD>{p.roleOnLoan}</TD>
              <TD>
                <span className="text-caption text-gray-mid italic">
                  Tracked in Vendor Program v3.0
                </span>
              </TD>
              <TD className="text-caption text-gray-mid italic">—</TD>
              <TD>{date(p.createdAt)}</TD>
              <TD className="text-caption text-gray-mid italic">—</TD>
              <TD>
                {/* Step 5 W1 — until now an assigned-but-unconnected vendor
                    looked identical to a fully connected one here: the Status
                    column reads "Active" for both, because it reports the
                    ASSIGNMENT, not the connection. The vendor meanwhile sees
                    an empty Customers tab and staff got no signal anywhere
                    outside a drawer nobody opens. */}
                {connectedAssignmentIds === undefined ? (
                  <span className="text-caption text-gray-mid italic">—</span>
                ) : connectedAssignmentIds.has(p.id) ? (
                  <Badge status="finalized">Connected</Badge>
                ) : p.isActive ? (
                  <Badge status="review">Not connected</Badge>
                ) : (
                  <span className="text-caption text-gray-mid italic">—</span>
                )}
              </TD>
              <TD>
                {p.isActive ? (
                  <Badge status="finalized">Active</Badge>
                ) : (
                  <Badge status="archived">Inactive</Badge>
                )}
              </TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}
